import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../contexts/ThemeContext';

interface Client {
  id: string;
  name: string;
}

interface StateHistory {
  id: string;
  client_id: string;
  client_name: string;
  previous_state: string;
  new_state: string;
  changed_at: string;
}

export default function HistoryPage() {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [history, setHistory] = useState<StateHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Add user profile state
  const [userFullName, setUserFullName] = useState<string>('');

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
      } else {
        // Load user profile name
        const metadata = session.user.user_metadata;
        const firstName = metadata.first_name || '';
        const lastName = metadata.last_name || '';
        setUserFullName(`${firstName} ${lastName}`.trim() || 'User');
      }
    };
    checkAuth();
  }, [navigate]);

  // Load clients
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from('clients')
      .select('id, name')
      .eq('user_id', session.user.id)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error loading clients:', error);
      return;
    }

    if (data) {
      setClients(data);
    }
  };

  const loadHistory = async (clientId: string) => {
    setLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from('client_state_history')
      .select('*')
      .eq('client_id', clientId)
      .eq('user_id', session.user.id)
      .order('changed_at', { ascending: true });

    if (error) {
      console.error('Error loading history:', error);
      setLoading(false);
      return;
    }

    if (data) {
      setHistory(data);
    }
    setLoading(false);
  };

  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(clientId);
    if (clientId) {
      loadHistory(clientId);
    } else {
      setHistory([]);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const selectedClient = clients.find(c => c.id === selectedClientId);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      {/* Navigation */}
      <nav className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-home-heart-line text-3xl text-navy"></i>
            </div>
            <span className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-navy'}`}>{userFullName || 'Loans with Heena'}</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <a href="/" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-home-5-line mr-2"></i>
              Home
            </a>
            <a href="/crm" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-contacts-book-2-line mr-2"></i>
              CRM
            </a>
            
            {/* Pages Dropdown */}
            <div className="relative group">
              <button className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center gap-1`}>
                <i className="ri-pages-line mr-2"></i>
                Pages
                <i className="ri-arrow-down-s-line"></i>
              </button>
              <div className={`absolute top-full left-0 mt-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-lg py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50`}>
                <a href="/closed" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-archive-line mr-2"></i>
                  Closed
                </a>
                <a href="/dead-deals" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-close-circle-line mr-2"></i>
                  Dead Deals
                </a>
                <a href="/on-hold" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-pause-circle-line mr-2"></i>
                  On Hold
                </a>
                <a href="/templates" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-file-text-line mr-2"></i>
                  Templates
                </a>
                <a href="/intake" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-inbox-line mr-2"></i>
                  Message Intake
                </a>
                <a href="/history" className={`block px-4 py-2 ${isDarkMode ? 'text-teal' : 'text-navy'} ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} font-semibold cursor-pointer`}>
                  <i className="ri-history-line mr-2"></i>
                  History
                </a>
                <a href="/admin" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-settings-3-line mr-2"></i>
                  Admin
                </a>
              </div>
            </div>
            
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-xl`}></i>
            </button>
            
            <button
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-logout-box-line mr-2"></i>
              Logout
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} hover:text-navy transition-colors cursor-pointer`}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}
          >
            <div className="px-6 py-4 space-y-3">
              <a href="/" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-home-5-line mr-2"></i>
                Home
              </a>
              <a href="/crm" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-contacts-book-2-line mr-2"></i>
                CRM
              </a>
              <a href="/closed" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-archive-line mr-2"></i>
                Closed
              </a>
              <a href="/dead-deals" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-close-circle-line mr-2"></i>
                Dead Deals
              </a>
              <a href="/on-hold" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-pause-circle-line mr-2"></i>
                On Hold
              </a>
              <a href="/templates" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-file-text-line mr-2"></i>
                Templates
              </a>
              <a href="/intake" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-inbox-line mr-2"></i>
                Message Intake
              </a>
              <a href="/history" className={`block py-2 ${isDarkMode ? 'text-teal' : 'text-navy'} font-semibold cursor-pointer`}>
                <i className="ri-history-line mr-2"></i>
                History
              </a>
              <a href="/admin" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-settings-3-line mr-2"></i>
                Admin
              </a>
              <button
                onClick={toggleDarkMode}
                className={`block w-full text-left py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}
              >
                <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} mr-2`}></i>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button
                onClick={handleLogout}
                className="block w-full text-left py-2 text-red-600 hover:text-red-700 transition-colors font-medium cursor-pointer"
              >
                <i className="ri-logout-box-line mr-2"></i>
                Logout
              </button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} mb-2`}>Client State History</h1>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>View all state changes for your clients</p>
        </div>

        {/* Client Selection */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mb-6`}>
          <label className={`block text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>Select a Client</label>
          <select
            value={selectedClientId}
            onChange={(e) => handleClientSelect(e.target.value)}
            className={`w-full px-4 py-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300 text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-base cursor-pointer`}
          >
            <option value="">-- Choose a client --</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>

        {/* History Display */}
        {selectedClientId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border overflow-hidden`}
          >
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700 bg-gradient-to-r from-teal/20 to-teal/30' : 'border-gray-200 bg-gradient-to-r from-teal/5 to-teal/10'}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-teal rounded-lg">
                  <i className="ri-user-line text-2xl text-white"></i>
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{selectedClient?.name}</h2>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{history.length} state changes recorded</p>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block w-8 h-8 border-4 border-teal border-t-transparent rounded-full animate-spin"></div>
                <p className={`mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading history...</p>
              </div>
            ) : history.length === 0 ? (
              <div className="p-12 text-center">
                <div className={`w-16 h-16 flex items-center justify-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-full mx-auto mb-4`}>
                  <i className={`ri-history-line text-3xl ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                </div>
                <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>No state changes recorded for this client yet.</p>
              </div>
            ) : (
              <div className="p-6">
                <div className="space-y-4">
                  {history.map((record, index) => (
                    <motion.div
                      key={record.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`relative pl-8 pb-6 border-l-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} last:border-l-0 last:pb-0`}
                    >
                      <div className={`absolute left-0 top-0 -translate-x-1/2 w-4 h-4 bg-teal rounded-full border-4 ${isDarkMode ? 'border-gray-800' : 'border-white'}`}></div>
                      <div className={`${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} rounded-lg p-4 transition-colors`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {record.previous_state ? (
                                <>
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                                    {record.previous_state}
                                  </span>
                                  <i className={`ri-arrow-right-line ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal/10 text-teal">
                                    {record.new_state}
                                  </span>
                                </>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal/10 text-teal">
                                  Initial: {record.new_state}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <i className="ri-time-line mr-1"></i>
                              {formatDate(record.changed_at)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Info Box */}
        {!selectedClientId && (
          <div className={`${isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} border rounded-xl p-4`}>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`ri-information-line text-xl ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}></i>
              </div>
              <div className="flex-1">
                <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} mb-1`}>About State History</h4>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Select a client from the dropdown above to view their complete state change history. 
                  This shows every time their status was updated, including the previous state, new state, 
                  and the exact date and time of the change.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
